import { useCallback } from 'react';
import { RestAPIEndpoints } from '@lifeomic/react-client';
import { apiHooks } from './useRestAPIHooks';

export type SendAgentFeedbackRequest =
  RestAPIEndpoints['POST /v1/agents-v2/feedback']['Request'];

export type SendAgentFeedbackInput = {
  feedback: SendAgentFeedbackRequest['feedback'];
  agentName: SendAgentFeedbackRequest['agent_name'];
  traceId: SendAgentFeedbackRequest['trace_id'];
  score?: SendAgentFeedbackRequest['score'];
  scoreName?: SendAgentFeedbackRequest['score_name'];
  /**
   * Whether the user also asked Zori to fix the issue (vs. leaving feedback
   * only). Surfaced in the agents Slack notification. Omitted when the feedback
   * has no fix/no-fix choice.
   */
  requestedFix?: boolean;
};

/**
 * `requested_fix` is accepted by the agents `/feedback` endpoint but not yet in
 * the generated `service-clients` schema, so widen the request body locally
 * until the schema is regenerated.
 */
type SendAgentFeedbackBody = SendAgentFeedbackRequest & {
  requested_fix?: boolean;
};

/** POST `/v1/agents-v2/feedback` via camelCase input → request body mapping. */
export function useSendAgentFeedback() {
  const sendFeedbackMutation = apiHooks.useAPIMutation(
    'POST /v1/agents-v2/feedback',
  );
  const sendAgentFeedback = useCallback(
    ({
      feedback,
      agentName,
      traceId,
      score,
      scoreName,
      requestedFix,
    }: SendAgentFeedbackInput) => {
      const body: SendAgentFeedbackBody = {
        feedback,
        agent_name: agentName,
        trace_id: traceId,
        ...(score !== undefined ? { score } : {}),
        ...(scoreName !== undefined ? { score_name: scoreName } : {}),
        ...(requestedFix !== undefined ? { requested_fix: requestedFix } : {}),
      };
      return sendFeedbackMutation.mutateAsync(body);
    },
    [sendFeedbackMutation],
  );
  return { sendAgentFeedback };
}
